/**
 * Polling authoritative checkout state.
 *
 * This is the only loop in the app, and its shape is set by MASTER_SPEC 9.6 and
 * Design Bible 13.3: the browser never decides what happened to a payment, it
 * asks core and waits. So this hook only ever issues a `GET`, it never retries
 * a command, and it stops as soon as core reports a state that is not going to
 * change on its own.
 *
 * It also stops after a bounded number of attempts. A tab left open overnight
 * on an order core cannot resolve should be a stated "we are still confirming,
 * here is what to do", not an infinite request loop — and the customer must
 * still not be offered a `Try again` that would re-enter payment.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AUTHORITATIVE_POLL_LIMIT, AUTHORITATIVE_POLL_MS } from "./retry.js";
import { checkoutStatusPath } from "./coreRoutes.js";
import { parseCheckoutState, type CheckoutState } from "./publicContract.js";
import { type Outcome, type Transport, correlationID, interpret } from "./transport.js";

export type CheckoutPhase =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly state: CheckoutState; readonly settled: boolean }
  | { readonly kind: "exhausted"; readonly state: CheckoutState }
  | { readonly kind: "failed"; readonly outcome: Outcome<never> };

/** States core will not move away from without someone doing something. */
function settledStatus(state: CheckoutState): boolean {
  return (
    state.status === "PAID" ||
    state.status === "EXPIRED" ||
    state.status === "CANCELLED"
  );
}

export interface CheckoutStatusOptions {
  readonly quoteID: string | null;
  readonly checkoutToken?: string | undefined;
  readonly csrfToken?: string | undefined;
  /** False while the customer is still filling the form in. */
  readonly enabled: boolean;
}

export interface CheckoutStatusResult {
  readonly phase: CheckoutPhase;
  readonly attempts: number;
  readonly refresh: () => void;
}

/** What the polling effect has learned, tagged with the quote it learned it for. */
interface Loaded {
  readonly quoteID: string;
  readonly phase: CheckoutPhase;
  readonly attempts: number;
}

export function useCheckoutStatus(
  transport: Transport,
  options: CheckoutStatusOptions
): CheckoutStatusResult {
  const { quoteID, checkoutToken, csrfToken, enabled } = options;
  /*
   * `idle` and `loading` are derived during render rather than written from the
   * effect. That keeps the effect a pure subscription to an external system —
   * it only ever publishes what core answered — and it means a change of quote
   * shows `loading` immediately instead of one render of the previous quote's
   * state.
   */
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [nonce, setNonce] = useState(0);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!enabled || quoteID === null) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    attemptsRef.current = 0;

    const publish = (next: CheckoutPhase): void => {
      setLoaded({ quoteID, phase: next, attempts: attemptsRef.current });
    };

    const poll = (): void => {
      attemptsRef.current += 1;
      void transport({
        path: checkoutStatusPath(quoteID),
        method: "GET",
        correlationID: correlationID(),
        ...(checkoutToken === undefined ? {} : { checkoutToken }),
        ...(csrfToken === undefined ? {} : { csrfToken })
      }).then((reply) => {
        if (cancelled) return;
        const outcome = interpret(reply, parseCheckoutState, "this checkout");
        if (outcome.kind !== "ok") {
          // An offline blip while polling is not a payment failure. It keeps
          // trying inside the attempt budget rather than reporting a state it
          // has not been told about.
          if (outcome.kind === "offline" && attemptsRef.current < AUTHORITATIVE_POLL_LIMIT) {
            timer = setTimeout(poll, AUTHORITATIVE_POLL_MS);
            return;
          }
          publish({ kind: "failed", outcome: outcome as Outcome<never> });
          return;
        }
        const settled = settledStatus(outcome.value);
        publish({ kind: "ready", state: outcome.value, settled });
        if (settled) return;
        if (attemptsRef.current >= AUTHORITATIVE_POLL_LIMIT) {
          publish({ kind: "exhausted", state: outcome.value });
          return;
        }
        timer = setTimeout(poll, AUTHORITATIVE_POLL_MS);
      });
    };

    poll();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [transport, quoteID, checkoutToken, csrfToken, enabled, nonce]);

  const refresh = useCallback(() => {
    setLoaded(null);
    setNonce((current) => current + 1);
  }, []);

  const phase: CheckoutPhase =
    !enabled || quoteID === null
      ? { kind: "idle" }
      : loaded === null || loaded.quoteID !== quoteID
        ? { kind: "loading" }
        : loaded.phase;

  return { phase, attempts: loaded?.attempts ?? 0, refresh };
}
